/**
 * ModalityCard — Input source selector chip for Document Studio
 *
 * Reusable card that represents one capture modality (File, Photo, Voice, Wearables).
 * Matches the area-selection card pattern from PhotoComplianceUpload.
 *
 * Mantine v8.x only. No MUI.
 */

import React from 'react';
import { Card, Stack, Group, Text, ThemeIcon, Badge } from '@mantine/core';

export interface ModalityCardProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  isActive: boolean;
  onClick: () => void;
  badge?: string | number;
  badgeColor?: string;
  comingSoon?: boolean;
  disabled?: boolean;
}

export const ModalityCard: React.FC<ModalityCardProps> = ({
  icon,
  label,
  description,
  isActive,
  onClick,
  badge,
  badgeColor = 'blue',
  comingSoon = false,
  disabled = false,
}) => {
  return (
    <Card
      withBorder
      p="sm"
      onClick={disabled ? undefined : onClick}
      style={{
        cursor: disabled ? 'default' : 'pointer',
        borderColor: isActive ? 'var(--mantine-color-blue-6)' : undefined,
        borderWidth: isActive ? 2 : 1,
        opacity: disabled ? 0.5 : 1,
        transition: 'border-color 0.15s ease',
        userSelect: 'none',
      }}
    >
      <Stack gap={6}>
        <Group justify="space-between" align="flex-start">
          <ThemeIcon
            variant="light"
            size="md"
            color={isActive ? 'blue' : 'gray'}
          >
            {icon}
          </ThemeIcon>
          <Group gap={4}>
            {badge !== undefined && (
              <Badge size="xs" color={badgeColor} variant="light">
                {badge}
              </Badge>
            )}
            {comingSoon && (
              <Badge size="xs" color="gray" variant="outline">
                Soon
              </Badge>
            )}
            {isActive && !comingSoon && (
              <Badge size="xs" color="blue" variant="filled">
                Active
              </Badge>
            )}
          </Group>
        </Group>
        <Text size="sm" fw={600}>
          {label}
        </Text>
        <Text size="xs" c="dimmed" lineClamp={2}>
          {description}
        </Text>
      </Stack>
    </Card>
  );
};
